import Uploadcare from 'uploadcare-widget';

const DEFAULT_OPTIONS = {
  previewStep: true,
  crop: 'free'
};

/**
 * Error class to handle file upload errors.
 * */
class FileUploadError extends Error {
  constructor(message, fileInfo) {
    super(message);

    this.name = 'FileUploadError';
    this.message = I18n.t('account.profile.notice.medium_upload_error');
    this.fileInfo = fileInfo;
  }
}

class FileUploadManager {
  /**
   * Uploadcare-based file picker via JS API
   *
   * @param {Object} [options] - additional configuration params
   *  Optional Options:
   *    onFileUploadStart -> callback function when each upload is started
   *    onFileUploadSuccess -> callback function when upload finishes
   *    onFileUploadError -> callback function when upload results in error
   */
  static pick(options = {}) {
    let widgetOptions = {
      publicKey: UPLOADCARE_PUBLIC_KEY,
      ...DEFAULT_OPTIONS,
      ...options
    };

    Uploadcare
      .openDialog(null, widgetOptions)
      .done(result => FileUploadManager.onDialogHide(result, widgetOptions));
  }

  static onDialogHide(result, options) {
    options.onFileUploadStart && options.onFileUploadStart();

    let hasMultipleUpload = (typeof result.files === 'function');

    if (hasMultipleUpload) {
      FileUploadManager.multipleUploadHandler(result, options);
      return;
    }

    FileUploadManager.singleUploadHandler(result, options);
  }

  static async multipleUploadHandler(result, options) {
    let uploadPromise = [];

    result.files().forEach(file => {
      let promise = new Promise((resolve, _) => {
        file.progress(uploadInfo => {
          if (uploadInfo['state'] === 'ready' && options.onFileUploadSuccess) {
            resolve(uploadInfo['incompleteFileInfo']);
          }
        });

        file.fail((error, fileInfo) => { FileUploadManager.errorHandler(error, fileInfo, options); });
      });

      uploadPromise.push(promise);
    });

    let response = await Promise.all(uploadPromise);
    options.onFileUploadSuccess && options.onFileUploadSuccess(response);
  }

  static singleUploadHandler(result, options) {
    result.progress(uploadInfo => {
      result.done(function(fileInfo) {
        options.onFileUploadSuccess(fileInfo);
      });
    });

    result.fail((error, fileInfo) => { FileUploadManager.errorHandler(error, fileInfo, options); });
  }

  /**
   * Error handler for the uploads.
   *  1) Builds an `FileUploadError` object.
   *  2) If the error callback is available, passes the error to the callback gracefully.
   *  3) Else, raises the error.
   * */
  static errorHandler(error, fileInfo, options) {
    let uploadError = new FileUploadError(error, fileInfo);

    if (options.onFileUploadError) {
      options.onFileUploadError(uploadError);
      return;
    }

    throw uploadError;
  }
}

export default FileUploadManager;



// WEBPACK FOOTER //
// ./app/assets/javascripts/lib/fileUploadManager.js